import { useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { useCompanyScopeContext } from "@ryde/redux";
import {
  editFormDataAF,
  EditFormMode,
  errorsCrudContentAtomFamily,
  getApplicationType,
  GlobalAppEventEnum,
  Log,
  rideInvestigationRideIdAtom,
  useDrawers,
  useHasPermissions,
  type ICoordinate,
  type IMapPlaceDto,
  type IStationDto,
} from "@ryde/shared";

import { MAPLIBRE_PLACE_INFO_STATION_ENTITY } from "./maplibre-place-info.constants";
import {
  buildPlaceInfoStationDraft,
  canOfferPlaceInfoStation,
} from "./maplibre-place-info.utils";

/** "Add station here": the new-station drawer, pre-filled from the clicked point and its place. */
export const useMapLibrePlaceInfoAddStation = () => {
  const { t } = useTranslation();
  const { eventEmitter } = useCompanyScopeContext();
  const { hasPermissions: canCreateStation } = useHasPermissions({
    entity: MAPLIBRE_PLACE_INFO_STATION_ENTITY,
    action: "create",
  });
  const { drawers, openEditFormDrawer } = useDrawers();
  const setStationDraft = useSetRecoilState(
    editFormDataAF([
      MAPLIBRE_PLACE_INFO_STATION_ENTITY,
      EditFormMode.UnboundDrawer,
    ]),
  );
  const setServerErrors = useSetRecoilState(
    errorsCrudContentAtomFamily(MAPLIBRE_PLACE_INFO_STATION_ENTITY),
  );
  // The ride investigation is a full-screen sibling above the drawers container; drawers open beneath it.
  const isInvestigationOpen = Boolean(
    useRecoilValue(rideInvestigationRideIdAtom),
  );
  const [isFullscreenKept, setIsFullscreenKept] = useState(false);

  /**
   * Settles only when the station is saved — a cancelled drawer never resolves — so no state may wait on
   * it. The one early end is a map that would not leave fullscreen, which `isFullscreenKept` reports.
   */
  const addStation = useEvent(
    async ({
      coordinate,
      place,
    }: {
      coordinate: ICoordinate;
      place: IMapPlaceDto | undefined;
    }): Promise<boolean> => {
      setIsFullscreenKept(false);

      // A fullscreen map shows only itself, and the drawer opens outside it — unseen until fullscreen ends.
      if (document.fullscreenElement) {
        await document.exitFullscreen().catch((error: unknown) => {
          Log.warn("Leaving fullscreen for the station drawer failed", error);
        });
      }

      // Still fullscreen, the drawer would open unseen and the button would look dead; the popup says why.
      if (document.fullscreenElement) {
        setIsFullscreenKept(true);
        return false;
      }

      setServerErrors(undefined);
      setStationDraft(buildPlaceInfoStationDraft({ coordinate, place }));

      const result = await openEditFormDrawer({
        entity: MAPLIBRE_PLACE_INFO_STATION_ENTITY,
        id: undefined,
        onCloseAction: () => setStationDraft(undefined),
      });

      if (!result.success || !result.data) {
        return false;
      }

      // The drawer resolves every entity's saved record as the same loose type; this one is a station.
      const station = result.data as IStationDto;

      // Screens that show stations put the new one up at once (the passengers map does).
      eventEmitter?.emit(GlobalAppEventEnum.STATION_CREATED, station);
      Log.successAndToast(
        t("ui.PLACE_INFO_STATION_ADDED", { name: station.name ?? "" }),
      );

      return true;
    },
  );

  return {
    canAddStation: canOfferPlaceInfoStation({
      applicationType: getApplicationType(),
      canCreateStation,
      drawers,
      areDrawersCovered: isInvestigationOpen,
    }),
    isFullscreenKept,
    addStation,
  };
};
